import { Link } from "react-router-dom";
import { Container, Eyebrow, MButton, ArrowRight, MarketingPage } from "./ui";
import { breadcrumb } from "./seo";
import { POSTS } from "./posts";

function Tag({ children }: { children: string }) {
  return (
    <span className="rounded-full bg-warm-accent/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-warm-accent">
      {children}
    </span>
  );
}

export function Blog() {
  const [featured, ...rest] = POSTS;
  return (
    <MarketingPage
      seo={{
        title: "Blog — WonderfulInterview",
        description:
          "Product updates, engineering deep-dives and our point of view on building fairer, more rigorous interviews with AI.",
        path: "/blog",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
        ]),
      }}
    >
      {/* Header */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-80"
          style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(244,201,155,0.45) 0%, rgba(244,201,155,0) 70%)" }}
        />
        <Container className="relative pt-20 pb-10 text-center sm:pt-28">
          <Eyebrow>The blog</Eyebrow>
          <h1 className="mx-auto mt-7 max-w-3xl text-balance text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            Notes on hiring, <span className="font-display italic font-normal text-warm-accent">automation</span> and trust.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-warm-muted">
            Product updates, engineering deep-dives and our point of view on building fairer interviews.
          </p>
        </Container>
      </section>

      {/* Featured */}
      <section className="pb-8">
        <Container>
          <Link
            to={`/blog/${featured.slug}`}
            className="group grid gap-8 overflow-hidden rounded-4xl border border-warm-border bg-warm-card transition-shadow hover:shadow-[0_30px_80px_-30px_rgba(36,31,25,0.3)] lg:grid-cols-2"
          >
            <div className="relative h-64 overflow-hidden lg:h-auto">
              <img
                src={featured.image}
                alt={featured.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
            </div>
            <div className="flex flex-col justify-center p-8 sm:p-12">
              <div className="flex items-center gap-3">
                <Tag>{featured.category}</Tag>
                <span className="text-sm text-warm-muted">
                  {featured.date} · {featured.read}
                </span>
              </div>
              <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-warm-ink sm:text-4xl">
                {featured.title}
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-warm-muted">{featured.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-warm-accent">
                Read article <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        </Container>
      </section>

      {/* Grid */}
      <section className="py-16">
        <Container>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group flex flex-col overflow-hidden rounded-3xl border border-warm-border bg-warm-card transition-shadow hover:shadow-[0_20px_50px_-25px_rgba(36,31,25,0.3)]"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-3">
                    <Tag>{p.category}</Tag>
                    <span className="text-xs text-warm-muted">{p.read}</span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold leading-snug text-warm-ink">{p.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-warm-muted">{p.excerpt}</p>
                  <span className="mt-4 text-xs text-warm-muted">{p.date}</span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* Newsletter CTA */}
      <section className="pb-24">
        <Container>
          <div className="rounded-4xl border border-warm-border bg-warm-sand/60 px-8 py-14 text-center sm:px-16">
            <h2 className="text-3xl font-bold tracking-tight text-warm-ink sm:text-4xl">
              Get our best writing in your inbox.
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-warm-muted">
              One thoughtful email a month on hiring, evaluation and building calm software. No spam, ever.
            </p>
            <form className="mx-auto mt-7 flex max-w-md flex-col gap-3 sm:flex-row" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                required
                aria-label="Email address"
                placeholder="you@company.com"
                className="w-full rounded-full border border-warm-border bg-warm-card px-5 py-3 text-sm text-warm-ink outline-none placeholder:text-warm-muted/60 focus:border-warm-accent"
              />
              <MButton href="#" className="shrink-0 px-6 py-3">
                Subscribe
              </MButton>
            </form>
          </div>
        </Container>
      </section>
    </MarketingPage>
  );
}
